import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { sessionsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/StatusBadge';
import { formatDate, formatEnergy, formatCurrency, formatDuration } from '@/lib/utils';
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { ArrowLeft, Clock, Zap, DollarSign, Battery, User, RefreshCw } from 'lucide-react';
import {
  Page, GlassCard, StatCard, SectionHeading, ErrorState, PageSkeleton, DetailRows,
  chartTooltipStyle, chartAxisProps, errorMessage, humanize
} from '@/components/glass/surfaces';

const SessionDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const energyGradientId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [session, setSession] = useState(null);
  const [meterValues, setMeterValues] = useState([]);
  const [reloadToken, setReloadToken] = useState(0);
  const fetchSeq = useRef(0);

  const fetchData = useCallback(async () => {
    const seq = ++fetchSeq.current;
    try {
      const [sessionRes, meterRes] = await Promise.all([
        sessionsApi.getById(id),
        sessionsApi.getMeterValues(id)
      ]);
      if (seq !== fetchSeq.current) return;
      setSession(sessionRes.data);
      setMeterValues(meterRes.data || []);
      setError('');
      setNotFound(false);
    } catch (fetchError) {
      if (seq !== fetchSeq.current) return;
      console.error('Failed to fetch session details:', fetchError);
      if (fetchError.response?.status === 404) {
        setNotFound(true);
        setSession(null);
        setError('');
      } else {
        setError(errorMessage(fetchError, 'Failed to load session details'));
      }
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setSession(null);
    setMeterValues([]);
    setNotFound(false);
    setError('');
    setLoading(true);
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData, reloadToken]);

  const series = (measurand, key, transform = (value) => value) => meterValues
    .filter((item) => item.measurand === measurand)
    .map((item) => ({
      time: new Date(item.timestamp).toLocaleTimeString(),
      [key]: transform(parseFloat(item.value))
    }))
    .filter((point) => Number.isFinite(point[key]));

  const energyData = series('Energy.Active.Import.Register', 'energy', (value) => value / 1000);
  const powerData = series('Power.Active.Import', 'power');

  if ((session && session.id !== id && String(session.transaction_id) !== id) || (loading && !session)) {
    return <PageSkeleton testId="session-detail-loading" cards={4} />;
  }

  if (notFound || (!session && !error)) {
    return (
      <Page testId="session-not-found">
        <ErrorState
          title="Session not found"
          message="This transaction is not available. It may have been removed or the link is wrong."
          onRetry={() => navigate('/sessions')}
          retryLabel="Back to sessions"
        />
      </Page>
    );
  }

  if (!session && error) {
    return (
      <Page testId="session-detail-page">
        <ErrorState message={error} onRetry={() => { setLoading(true); setReloadToken((value) => value + 1); }} />
      </Page>
    );
  }

  const hasCost = session.cost !== null && session.cost !== undefined;

  return (
    <Page testId="session-detail-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <Button type="button" variant="ghost" size="icon" className="mt-1 shrink-0 rounded-full" onClick={() => navigate('/sessions')} data-testid="back-btn" aria-label="Back to sessions">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-3xl font-semibold tracking-tight">Transaction #{session.transaction_id}</h1>
              <StatusBadge status={session.is_active ? 'Charging' : 'Finished'} />
            </div>
            <p className="mt-1 break-all text-sm text-muted-foreground sm:text-base">
              {session.charge_point_id} · Connector {session.connector_id}
            </p>
          </div>
        </div>
        <Button type="button" variant="outline" className="rounded-full" onClick={() => setReloadToken((value) => value + 1)} aria-label="Refresh session">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {error ? <ErrorState title="Could not refresh this session" message={error} onRetry={() => setReloadToken((value) => value + 1)} /> : null}

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatCard label="Energy" value={formatEnergy(session.energy_kwh)} icon={Zap} tone="warning" />
        <StatCard label="Cost" value={formatCurrency(session.cost, session.currency)} icon={DollarSign} tone="success" />
        <StatCard label="Duration" value={formatDuration(session.start_timestamp, session.stop_timestamp)} icon={Clock} tone="info" />
        <StatCard label="ID tag" value={session.id_tag || '-'} icon={User} tone="neutral" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <GlassCard className="min-w-0 p-5 sm:p-6">
            <SectionHeading icon={Zap} title="Energy" description="Cumulative register, converted from Wh to kWh." />
            {energyData.length === 0 ? (
              <div className="flex h-[240px] flex-col items-center justify-center text-center">
                <Zap className="mb-2 h-6 w-6 text-muted-foreground" />
                <p className="text-sm font-medium">No energy samples</p>
                <p className="mt-1 text-xs text-muted-foreground">Meter values have not been reported for this session.</p>
              </div>
            ) : (
              <div className="h-[240px] min-w-0 sm:h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={energyData}>
                    <defs>
                      <linearGradient id={energyGradientId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--chart-3))" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="hsl(var(--chart-3))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="4 8" vertical={false} />
                    <XAxis dataKey="time" {...chartAxisProps} />
                    <YAxis {...chartAxisProps} width={40} />
                    <Tooltip contentStyle={chartTooltipStyle} formatter={(value) => [`${Number(value).toFixed(2)} kWh`, 'Energy']} />
                    <Area type="monotone" dataKey="energy" stroke="hsl(var(--chart-3))" fill={`url(#${energyGradientId})`} strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </GlassCard>

          <GlassCard className="min-w-0 p-5 sm:p-6">
            <SectionHeading icon={Battery} title="Power" description="Active import during the session." />
            {powerData.length === 0 ? (
              <div className="flex h-[240px] flex-col items-center justify-center text-center">
                <Battery className="mb-2 h-6 w-6 text-muted-foreground" />
                <p className="text-sm font-medium">No power samples</p>
              </div>
            ) : (
              <div className="h-[240px] min-w-0 sm:h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={powerData}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="4 8" vertical={false} />
                    <XAxis dataKey="time" {...chartAxisProps} />
                    <YAxis {...chartAxisProps} width={40} />
                    <Tooltip contentStyle={chartTooltipStyle} formatter={(value) => [`${Number(value).toFixed(2)} kW`, 'Power']} />
                    <Line type="monotone" dataKey="power" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </GlassCard>
        </div>

        <div className="space-y-4">
          <GlassCard className="p-5 sm:p-6">
            <SectionHeading title="Details" />
            <DetailRows
              rows={[
                {
                  label: 'Charger',
                  value: (
                    <button type="button" className="font-mono text-primary underline-offset-4 hover:underline" onClick={() => navigate(`/chargers/${session.charge_point_id}`)}>
                      {session.charge_point_id}
                    </button>
                  )
                },
                { label: 'Connector', value: <span className="font-mono">{session.connector_id}</span> },
                { label: 'Start', value: formatDate(session.start_timestamp) },
                session.stop_timestamp ? { label: 'End', value: formatDate(session.stop_timestamp) } : null,
                { label: 'Meter start', value: <span className="font-mono">{session.meter_start ?? '-'} Wh</span> },
                session.meter_stop != null ? { label: 'Meter stop', value: <span className="font-mono">{session.meter_stop} Wh</span> } : null,
                session.stop_reason ? { label: 'Stop reason', value: humanize(session.stop_reason) } : null,
                session.remote_request_id ? { label: 'Remote start', value: 'Yes' } : null
              ]}
            />
          </GlassCard>

          {hasCost ? (
            <GlassCard className="p-5 sm:p-6">
              <SectionHeading title="Cost" description="Charged from the tariff assigned to this charger." />
              <DetailRows
                rows={[
                  { label: 'Energy', value: formatEnergy(session.energy_kwh) },
                  { label: 'Currency', value: session.currency || 'AED' },
                  { label: 'Total', value: <span className="font-mono text-base">{formatCurrency(session.cost, session.currency)}</span> }
                ]}
              />
            </GlassCard>
          ) : null}
        </div>
      </div>
    </Page>
  );
};

export default SessionDetailPage;
